import cx from 'clsx';
import React from 'react';
import SelectComponent, { type SelectRenderer } from 'react-dropdown-select';
import { type Control, useController } from 'react-hook-form';

import useTranslation from '@hooks/useTranslation';

import { selectAppRtl } from '@store/useAppStore';

import style from './style.module.scss';

export interface ItemKeys<T> {
  valueFieldKey: string;
  labelFieldKey: string;
  disabledKey?: string;
}

interface ICommonProps<T> {
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  itemKeys?: ItemKeys<T>;
  searchable?: boolean;
  options: T[];
  loadingList?: boolean;
  type?: 'woHelperText';
  dataQa?: string;
  className?: string;
  labelRender?: (item: T) => React.ReactNode;
  searchFn?: (search: string) => T[];
  noDataRenderer?: ({ props, state, methods }: SelectRenderer<T>) => JSX.Element;
}

export interface IUncontrolledProps<T> extends ICommonProps<T> {
  value: T;
  onChange: (value: T) => void;
  error?: any;
}

export interface IControlledProps<T> extends ICommonProps<T> {
  name: string;
  control: Control<any>;
}

const Component = <T extends object>({
  value,
  options,
  error,
  label = '',
  placeholder = '',
  required = false,
  disabled = false,
  itemKeys = { valueFieldKey: 'id', labelFieldKey: 'name', disabledKey: '' },
  searchable = false,
  loadingList = false,
  type,
  dataQa,
  className,
  labelRender,
  onChange,
  searchFn,
  noDataRenderer,
}: IUncontrolledProps<T>) => {
  const { t } = useTranslation();

  const rtl = selectAppRtl();

  const { valueFieldKey, labelFieldKey, disabledKey } = itemKeys;

  const getItemLabel = (item: any) => {
    if (labelRender) {
      return labelRender(item);
    }

    return item[labelFieldKey];
  };

  return (
    <div className={cx(style.selectWrapper, className)} data-qa={dataQa}>
      {label && (
        <div
          className={cx(style.selectLabel, {
            [style.required]: required,
          })}
        >
          {label}
        </div>
      )}
      <SelectComponent
        multi={false}
        className={cx(style.select, {
          [style.selectFilled]: !!value,
          [style.selectError]: !!error,
          [style.selectDisabled]: disabled,
        })}
        placeholder={placeholder}
        itemRenderer={({ item, methods: { addItem, isSelected } }) => (
          <div
            className={cx(style.selectItem, {
              [style.activeItem]: isSelected(item),
              [style.rtl]: rtl,
              [style.disabledItem]: disabledKey && item[disabledKey as keyof T],
            })}
            onClick={() => addItem(item)}
          >
            {getItemLabel(item)}
          </div>
        )}
        valueField={valueFieldKey}
        labelField={labelFieldKey}
        values={[value]}
        options={options}
        onChange={(values) => onChange(values[0])}
        disabled={disabled}
        searchable={searchable}
        searchFn={searchable && searchFn ? ({ state }) => searchFn(state.search) : undefined}
        noDataRenderer={noDataRenderer}
        loading={loadingList}
        searchBy={labelFieldKey}
        dropdownGap={5}
      />
      {type !== 'woHelperText' && (
        <div className={style.helperText}>
          {error && t(error?.message || error?.[valueFieldKey]?.message)}
        </div>
      )}
    </div>
  );
};

const Controlled = <T extends object>({
  name,
  control,
  options,
  label,
  placeholder,
  required,
  disabled,
  itemKeys,
  searchable,
  loadingList,
  type,
  dataQa,
  className,
  labelRender,
  searchFn,
  noDataRenderer,
}: IControlledProps<T>) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  return (
    <Component
      value={value}
      options={options}
      onChange={onChange}
      error={error}
      label={label}
      labelRender={labelRender}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      itemKeys={itemKeys}
      searchable={searchable}
      searchFn={searchFn}
      noDataRenderer={noDataRenderer}
      loadingList={loadingList}
      type={type}
      dataQa={dataQa || name}
      className={className}
    />
  );
};

const Select = <T extends object>(props: IControlledProps<T> | IUncontrolledProps<T>) => {
  if ('control' in props) {
    return <Controlled<T> {...props} />;
  }

  return <Component<T> {...props} />;
};

export default Select;
